import React from "react";
import {
  MdCake,
  MdCheckCircle,
  MdEdit,
  MdEmail,
  MdLocationOn,
  MdPhone,
  MdVolunteerActivism,
  MdWork,
} from "react-icons/md";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button from "components/button";
import { formatDate } from "utils/format";

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" />
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-fg-muted">{label}</dt>
        <dd className="break-words text-sm font-medium text-fg">{value}</dd>
      </div>
    </div>
  );
};

const Group = ({ title, children }) => (
  <section className="border-b border-line/60 py-4 last:border-b-0">
    <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">{title}</h2>
    <dl>{children}</dl>
  </section>
);

const ProfileVerifyStep = ({ profileData, onConfirm, onEdit, isSubmitting }) => {
  const fullName = `${profileData.firstName} ${profileData.lastName}`;
  const address = [profileData.address, profileData.city, profileData.state, profileData.zip]
    .filter(Boolean)
    .join(", ");
  const hasPreferences = profileData.hobbies || profileData.favoriteFood || profileData.interests;
  const isStaff = profileData.role === "staff";

  return (
    <>
      <div className="text-center">
        <h1 className="text-title">Verify Your Information</h1>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          Please review your information before starting the assessment.
        </p>
      </div>

      <div className="mt-6 flex items-center gap-4 border-b border-line/60 pb-4">
        <Avatar src={profileData.avatarPreview} name={fullName} size="lg" />
        <div>
          <p className="text-heading">{fullName}</p>
          <Badge tone="brand" className="mt-1">
            {isStaff ? (
              <MdWork aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <MdVolunteerActivism aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            {isStaff ? "Staff" : "Volunteer"}
          </Badge>
        </div>
      </div>

      <Group title="Contact Information">
        <InfoRow icon={MdEmail} label="Email" value={profileData.email} />
        <InfoRow icon={MdPhone} label="Phone" value={profileData.phone} />
      </Group>

      <Group title="Personal Details">
        <InfoRow
          icon={MdCake}
          label="Date of Birth"
          value={profileData.dob ? formatDate(profileData.dob) : ""}
        />
        <InfoRow icon={MdWork} label="Job Title / Ministry Role" value={profileData.jobTitle} />
      </Group>

      <Group title="Address">
        <InfoRow icon={MdLocationOn} label="Location" value={address} />
      </Group>

      {hasPreferences && (
        <section className="py-4">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
            Preferences
          </h2>
          <dl className="flex flex-col gap-3 text-sm">
            {profileData.hobbies && (
              <div>
                <dt className="text-xs text-fg-muted">Hobbies & Activities</dt>
                <dd className="mt-0.5 text-fg">{profileData.hobbies}</dd>
              </div>
            )}
            {profileData.favoriteFood && (
              <div>
                <dt className="text-xs text-fg-muted">Favorite Food & Restaurants</dt>
                <dd className="mt-0.5 text-fg">{profileData.favoriteFood}</dd>
              </div>
            )}
            {profileData.interests && (
              <div>
                <dt className="text-xs text-fg-muted">Other Interests</dt>
                <dd className="mt-0.5 text-fg">{profileData.interests}</dd>
              </div>
            )}
          </dl>
        </section>
      )}

      <div className="mt-6 flex flex-col-reverse gap-3">
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          leftIcon={<MdEdit aria-hidden="true" />}
          onClick={onEdit}
          disabled={isSubmitting}
        >
          Edit Information
        </Button>
        <Button
          size="lg"
          fullWidth
          leftIcon={<MdCheckCircle aria-hidden="true" />}
          onClick={onConfirm}
          loading={isSubmitting}
        >
          {isSubmitting ? "Saving…" : "Confirm & Start Assessment"}
        </Button>
      </div>
    </>
  );
};

export default ProfileVerifyStep;
